import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { SignalPill, type MissionSignal } from './MissionSurface';

/** 콘텐츠 컨테이너. 기본 max-w 1240 중앙(03 §4.2). wide=풀블리드(보드/그래프).
 *  여백은 밀도 토큰(.view-pad) — data-density=compact에서 자동 축소. */
export function ViewContainer({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className={cn('view-pad animate-rise', wide ? 'max-w-none' : 'mx-auto max-w-[1240px]')}>
      {children}
    </div>
  );
}

/**
 * 콘솔 컨텍스트 바 — 뷰 최상단 1줄(sticky, TopBar 바로 아래 61px).
 * eyebrow·제목·라이브 지표·액션을 한 줄로 압축해 (구) ViewHeader+MissionBrief 도입부(~250px)를
 * 대체하고, 작업 표면을 화면 꼭대기로 끌어올린다. filters=보조 필터/검색 줄(선택).
 */
export function ViewBar({
  eyebrow,
  title,
  signals,
  actions,
  filters,
  flush = false,
  tight = false,
}: {
  eyebrow?: string;
  title: string;
  signals?: MissionSignal[];
  actions?: ReactNode;
  filters?: ReactNode;
  /** true = 앱 TopBar 오프셋(61px) 없이 자기 컨테이너 최상단에 붙는다 — 스크롤을 자기가 쥔 뷰(독서실)·전체화면 오버레이용. */
  flush?: boolean;
  /** true = 좁은 화면에서 한 줄로 압축(eyebrow 숨김·제목 축약). md 이상은 기본과 동일. */
  tight?: boolean;
}) {
  return (
    <div
      className={cn(
        'bar-pad sticky z-20 border-b border-line backdrop-blur-xl',
        flush ? 'top-0' : 'top-[61px]',
      )}
      style={{ background: 'rgba(8,10,18,.82)' }}
    >
      <div className={cn('flex items-center gap-x-3 gap-y-2', tight ? 'flex-nowrap md:flex-wrap' : 'flex-wrap')}>
        <div className={cn('flex items-baseline gap-2', tight ? 'min-w-0 flex-1 md:flex-none' : 'flex-none')}>
          {eyebrow && (
            <span
              className={cn(
                'font-mono text-[10px] uppercase tracking-[0.2em] text-faint',
                tight && 'hidden md:inline',
              )}
            >
              {eyebrow}
            </span>
          )}
          <h2 className={cn('font-serif text-[16px] font-bold leading-tight text-ink', tight && 'min-w-0 truncate')}>
            {title}
          </h2>
        </div>
        {signals && signals.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 border-l border-line pl-3">
            {signals.map((s) => (
              <SignalPill key={s.label} {...s} />
            ))}
          </div>
        )}
        {actions && <div className="ml-auto flex flex-none items-center gap-2">{actions}</div>}
      </div>
      {filters && <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">{filters}</div>}
    </div>
  );
}

/**
 * 콘솔 본문 패널. 일반 카드보다 헤더·본문 경계를 명확히 하고, 작업 표면의
 * 단위(원장, 큐, 매트릭스, 캔버스)를 같은 밀도로 맞춘다.
 */
export function ConsolePanel({
  label,
  title,
  meta,
  actions,
  children,
  className,
  bodyClassName,
  variant = 'default',
}: {
  label?: string;
  title?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** 위계 — primary=주 작업면(부각), rail=보조 레일(가라앉음), default=일반 */
  variant?: 'default' | 'primary' | 'rail';
}) {
  const hasHeader = label || title || meta || actions;
  return (
    <section
      className={cn(
        'surface-hl overflow-hidden rounded-lg border',
        variant === 'rail' && 'border-line/70 bg-bg/50', // 가라앉은 보조
        variant === 'primary' && 'border-line-2/70 bg-panel/80', // 부각된 주역
        variant === 'default' && 'border-line bg-bg-2/70',
        className,
      )}
    >
      {hasHeader && (
        <div className="flex min-h-10 items-center gap-3 border-b border-line bg-panel/45 px-3 py-2">
          <div className="min-w-0 flex-1">
            {label && <div className="field-key">{label}</div>}
            {title && <div className="truncate text-[14px] font-semibold text-ink">{title}</div>}
          </div>
          {meta && <div className="flex flex-none items-center gap-2">{meta}</div>}
          {actions && <div className="flex flex-none items-center gap-1.5">{actions}</div>}
        </div>
      )}
      <div className={cn('pad-card-sm', bodyClassName)}>{children}</div>
    </section>
  );
}

export function RailButton({
  active,
  children,
  count,
  color,
  onClick,
  ariaLabel,
}: {
  active?: boolean;
  children: ReactNode;
  count?: ReactNode;
  color?: string;
  onClick?: () => void;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        'flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-left text-xs transition',
        active
          ? 'border-star bg-star/10 text-star'
          : 'border-line bg-bg-2/40 text-faint hover:border-line-2 hover:text-dim',
      )}
    >
      {color && <span className="size-1.5 flex-none rounded-full" style={{ background: color }} />}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {count != null && <span className="font-mono text-[10px] tabular-nums text-fainter">{count}</span>}
    </button>
  );
}

/** 뷰 헤더: eyebrow + h2(serif) + 설명 + 우측 액션 */
export function ViewHeader({
  eyebrow,
  title,
  desc,
  actions,
}: {
  eyebrow?: string;
  title: string;
  desc?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-[26px] flex items-end justify-between gap-4">
      <div>
        {eyebrow && <div className="eyebrow mb-1.5">{eyebrow}</div>}
        <h2 className="font-serif text-[30px] font-bold leading-[1.2] tracking-[-0.01em] text-white">
          {title}
        </h2>
        {desc && <p className="mt-2 max-w-[70ch] text-sm text-dim">{desc}</p>}
      </div>
      {actions && <div className="flex flex-none items-center gap-2">{actions}</div>}
    </header>
  );
}

/** 미구현 모듈 placeholder — 궤도 일러스트 + 마일스톤 표기 (M0 셸 검증용) */
export function Placeholder({
  eyebrow,
  title,
  desc,
  milestone,
}: {
  eyebrow?: string;
  title: string;
  desc?: string;
  milestone: string;
}) {
  return (
    <ViewContainer>
      <ViewHeader eyebrow={eyebrow} title={title} desc={desc} />
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 rounded-xl border border-line bg-panel/40 p-12 text-center">
        <svg width="68" height="68" viewBox="0 0 68 68" fill="none" aria-hidden="true">
          <circle cx="34" cy="34" r="3.5" fill="var(--star)" />
          <circle cx="34" cy="34" r="16" stroke="var(--line-2)" strokeWidth="1" />
          <circle cx="34" cy="34" r="27" stroke="var(--line)" strokeWidth="1" strokeDasharray="3 5" />
          <circle cx="50" cy="34" r="2" fill="var(--star-2)" />
          <circle cx="34" cy="7" r="1.6" fill="var(--p-arcadia)" />
        </svg>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-faint">
          {milestone} 에서 구현됩니다
        </p>
        <p className="max-w-sm text-sm text-dim">
          데이터·안전 코어를 먼저 잠그고, 이 뷰는 곧 별을 채웁니다.
        </p>
      </div>
    </ViewContainer>
  );
}
